import { nanoid } from "@repo/shared/utils/id";
import { generateKeyBetween } from "fractional-indexing";

import { datasets$ } from "../datasets/datasets.crud";
import { activeOrganization$ } from "../organizations";
import { datasetFolders$ } from "./dataset-folders.crud";
import type { DatasetFolder } from "./dataset-folders.types";

/** Folders of the active org, sorted by their fractional-index `order` key. */
export const getSortedDatasetFolders = (): DatasetFolder[] => {
  const all = Object.values(datasetFolders$.get() ?? {}).filter(
    (folder): folder is DatasetFolder => folder != null,
  );
  return all.sort((a, b) =>
    a.order < b.order ? -1 : a.order > b.order ? 1 : 0,
  );
};

/**
 * Appends a folder after the last existing one. Returns the new folder id so
 * the caller can put it straight into rename mode.
 */
export const createDatasetFolder = (name = ""): string => {
  const organizationId = activeOrganization$.id.get() as string;
  const id = nanoid();
  const lastOrder = getSortedDatasetFolders().at(-1)?.order ?? null;

  datasetFolders$[id].set({
    id,
    organizationId,
    name,
    order: generateKeyBetween(lastOrder, null),
    createdAt: undefined as never,
    updatedAt: undefined as never,
  });

  return id;
};

export const renameDatasetFolder = (folderId: string, name: string) => {
  datasetFolders$[folderId].name.set(name);
};

/**
 * Deletes a folder. Datasets filed under it are unfiled — the server's
 * `set null` foreign key does this authoritatively, and the local `folderId`
 * is cleared here so the page reflows immediately.
 */
export const deleteDatasetFolder = (folderId: string) => {
  for (const datasetId of Object.keys(datasets$)) {
    if (datasets$[datasetId].folderId.get() === folderId) {
      datasets$[datasetId].folderId.set(null);
    }
  }
  datasetFolders$[folderId].delete();
};

/** Moves a dataset into a folder, or unfiles it when `folderId` is `null`. */
export const moveDatasetToFolder = (
  datasetId: string,
  folderId: string | null,
) => {
  if (datasets$[datasetId].folderId.get() === folderId) return;
  datasets$[datasetId].folderId.set(folderId);
};

/**
 * Reorders a folder to a new position within the sorted folder list, using
 * fractional-indexing to compute a key between its two new neighbors.
 */
export const reorderDatasetFolder = (
  folderId: string,
  newIndex: number,
  sortedIds: string[],
) => {
  const without = sortedIds.filter((id) => id !== folderId);
  if (newIndex < 0 || newIndex > without.length) return;
  const prevOrder =
    newIndex === 0
      ? null
      : (datasetFolders$[without[newIndex - 1]].order.get() ?? null);
  const nextOrder =
    newIndex >= without.length
      ? null
      : (datasetFolders$[without[newIndex]].order.get() ?? null);
  datasetFolders$[folderId].order.set(generateKeyBetween(prevOrder, nextOrder));
};
